'use strict';
/* global VXConfig */
import FluxEventEmitter from '../FluxEventEmitter';
import assign from 'object-assign';
import { doFetch, urlWithQuery } from '../../utils/CommonUtils';
import Flux from '../Flux';
import { trackPurchase } from '../../utils/DataLayerHelper';
import EnumGA4Events from '../../utils/EnumGA4Events';
import Constants from '../Constants.js';

const VXMODELS_FEED_URL = VXConfig.services.vxfeed.url;
const VXMODELS_TOKEN = VXConfig.services.vxfeed.accessToken;
const _posts = [];
const _selectedPostId = [];
const _hasNextPage = [];
const _postsSignature = [];
let _isVideoMuted = true;
let _isBlockVideoMuted = true;
let _activeProfileActorId = null;

function getPostsCacheKey(feedType, actorId) {
	return actorId ? `${feedType}:${actorId}` : feedType;
}

function isPostsCacheUsable(feedType, actorId, signature) {
	if (feedType === Constants.Feed.FeedType.PROFILE && actorId !== _activeProfileActorId) {
		return false;
	}
	return signature === null || _postsSignature[getPostsCacheKey(feedType, actorId)] === signature;
}

const CHANGE_EVENTS = {
	FeedOverlay: 'feedOverlay',
	FeedLike: 'feedLike',
	UpdatePost: 'updatePost',
	SetPosts: 'setPosts',
	VideoBlockPlay: 'videoBlockPlay',
	VideoPlay: 'videoPlay',
};

const POST_PRICE = 'priceCoins';
const TIPPING_GOAL = {
	goal: 'goalCoins',
	tipsSum: 'tipsSumCoins',
};

const getGuestFSK = () => (Flux.Guest.hasAvs() ? 'FSK18' : 'FSK16');

const getContextByFeedType = (feedType) => {
	switch (feedType) {
		case Constants.Feed.FeedType.PROFILE:
			return Constants.Feed.Context.PROFILE;
		case Constants.Feed.FeedType.MIXED:
			return Constants.Feed.Context.MIXED_FEED;
		default:
			return Constants.Feed.Context.UNKNOWN;
	}
};

/**
 *
 * @param {string} query
 * @param {Object} variables
 * @param {Object} queryParam
 * @returns {Promise}
 */
function doVxModelsRequest(query, variables = null, queryParam = {}) {
	const queryObject = {};
	queryObject.query = query;

	if (variables) {
		queryObject.variables = variables;
	}

	const requestUrl = urlWithQuery(VXMODELS_FEED_URL, {
		...queryParam,
	});

	const init = {
		headers: { Authorization: 'Bearer ' + (Flux.Guest.isLoggedIn() ? VXMODELS_TOKEN : '') },
	};

	return doFetch(requestUrl, queryObject, Constants.HttpMethods.POST, false, init);
}

const FeedStore = assign({}, FluxEventEmitter.prototype, {
	EVENTS: CHANGE_EVENTS,

	getFeedData(args, callback, withTotal) {
		let total = ``;
		let modelIdType = 'ID';
		args.language = VXConfig.language || Constants.Languages.DE;

		if (withTotal) {
			modelIdType = 'ID!';
			total = `
			total: feedTotalCounts(modelId: $modelId) {
				totalCount
			}
		`;
		}
		const query = `
		query GetFeed($first: Int!, $after: String, $modelId: ${modelIdType}, $follows: Boolean, $bought: Boolean, $typeGroups: [PostTypeGroupEnum!], $mode: FeedModeEnum, $liked: Boolean, $commented: Boolean, $language: String) {
			feed(modelId: $modelId, typeGroups: $typeGroups, follows: $follows, bought: $bought, mode: $mode, liked: $liked, commented: $commented) {
			  id
			  posts(first: $first, after: $after, language: $language) {
				pageInfo {
					hasNextPage
				}
				edges {
				  cursor
				  node {
					${post()}
				  }
				}
				pageInfo {
				  hasNextPage
				}
			  }
			}
			${total}
		  }
		`;
		doVxModelsRequest(query, { ...args }).then(callback);
	},
	getFeedTotals(args, callback) {
		const query = `
		query GetFeedTotal($modelId: ID!, $follows: Boolean, $bought: Boolean, $liked: Boolean, $commented: Boolean) {
			counts: feedTotalCounts(modelId: $modelId, follows: $follows, bought: $bought, liked: $liked, commented: $commented) {
				totalCount
				clips
				photos
				texts
				liked
				commented
			  }
			bought: feedTotalCounts(modelId: $modelId, bought: true) {
				totalCount
				clips
				photos
				texts
				liked
				commented
			}
		  }
		`;
		doVxModelsRequest(query, { ...args }).then(callback);
	},
	getFeedGuestTotals(args, callback) {
		const query = `
query GetFeedGuestTotals($guestId: Int!) {
  stats {
    boughtContentGuest(guestId: $guestId) {
      photos
      clips
    }
  }
}
	`;
		doVxModelsRequest(query, { ...args }).then((data) => {
			callback(data?.data?.stats?.boughtContentGuest?.[0] || {});
		});
	},
	getFeedPost(postId, callback) {
		const query = `
	query GetPost($postId: ID!) {
		post(postId: $postId) {
			${post()}
		}
	  }
	`;
		doVxModelsRequest(query, { postId }).then(callback);
	},
	viewedPost(postId, feedType, callback) {
		const mutation = `
	mutation viewedPost($postId: ID!, $context: ContextEnum!) {
		vx {
			viewedPost(postId: $postId, context: $context) {
			  id
			}
		}
	  }
	`;
		doVxModelsRequest(mutation, { postId, context: getContextByFeedType(feedType) }).then(callback);
	},
	buyPost(postId, callback) {
		const mutation = `
	mutation buyPost($postId: ID!) {
		vx {
			buyPost(postId: $postId) {
			  success
			}
		}
	  }
	`;
		doVxModelsRequest(mutation, { postId })
			.then((response) => {
				// track success purchase
				if (response.data && response.data.vx.buyPost.success) {
					trackPurchase({
						[EnumGA4Events.DEFAULT_PARAMETER_TRANSACTION_ID]: postId + ':' + Flux.Guest.getId(),
						[EnumGA4Events.DEFAULT_PARAMETER_ITEMS]: [
							{
								[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_NAME]: EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_NAME__VALUE_FEED,
								[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_CATEGORY]:
									EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_CATEGORY__VALUE_FEED_PAID_CONTENT,
							},
						],
					});
				}
				return response;
			})
			.then(callback);
	},
	likePost(postId, feedType, callback) {
		const mutation = `
	mutation likePost($postId: ID!, $context: ContextEnum!) {
		vx {
			likePost(postId: $postId, context: $context) {
			  id
			}
		}
	  }
	`;
		doVxModelsRequest(mutation, { postId, context: getContextByFeedType(feedType) }).then(callback);
	},
	unlikePost(postId, callback) {
		const mutation = `
	mutation unlikePost($postId: ID!) {
		vx{
			unlikePost(postId: $postId) {
			  id
			}
		}
	  }
	`;
		doVxModelsRequest(mutation, { postId }).then(callback);
	},
	commentPost(postId, text, repliesToCommentId, callback) {
		const mutation = `
	mutation commentPost($postId: ID!, $text: String!, $repliesToCommentId: ID) {
		vx {
			commentPost(postId: $postId, text: $text, repliesToCommentId: $repliesToCommentId) {
			  id
			}
		}
	  }
	`;
		doVxModelsRequest(mutation, { postId, text, repliesToCommentId }).then(callback);
	},
	deleteComment(commentID, callback) {
		const mutation = `
	mutation deleteComment($commentID: ID!) {
		vx {
			deleteComment(commentID: $commentID)
		}
	  }
	`;
		doVxModelsRequest(mutation, { commentID }).then(callback);
	},
	addTip(postId, amount, { item_category = EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_CATEGORY__VALUE_FEED_TIP }, callback) {
		const amountCoins = amount;
		amount = null;

		const mutation = `
    mutation tipPost($postId: ID!, $amount: Euro, $amountCoins: Coins) {
        vx {
            tipPost(postId: $postId, amount: $amount, amountCoins: $amountCoins) {
              success
            }
        }
    }
	`;

		doVxModelsRequest(mutation, { postId, amount, amountCoins })
			.then((response) => {
				// track success purchase
				if (response.data && response.data.vx.tipPost.success) {
					trackPurchase({
						[EnumGA4Events.DEFAULT_PARAMETER_TRANSACTION_ID]: postId + ':' + Flux.Guest.getId(),
						[EnumGA4Events.DEFAULT_PARAMETER_VALUE]: amountCoins,
						[EnumGA4Events.DEFAULT_PARAMETER_ITEMS]: [
							{
								[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_NAME]: EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_NAME__VALUE_FEED,
								[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_CATEGORY]: item_category,
							},
						],
					});
				}
				return response;
			})
			.then(callback);
	},
	// `signature` identifies the filter the posts were fetched with, so a cached page (and its
	// load-more cursor) is never reused under a different filter. See getPosts/getHasNextPage.
	setPosts(posts, feedType, hasNextPage = false, actorId = null, signature = null) {
		const key = getPostsCacheKey(feedType, actorId);
		_posts[key] = posts;
		_hasNextPage[key] = posts.length === 0 ? false : hasNextPage;
		_postsSignature[key] = signature;
	},
	// A profile feed is cached so switching tabs within the same actor's profile doesn't refetch.
	// Call this once a profile feed component actually commits (componentDidMount), not from a
	// constructor: the constructor runs during React's render phase, and a discarded or
	// double-invoked render (StrictMode, Suspense retry) must not be allowed to advance
	// _activeProfileActorId or drop another actor's cache before the visit is real.
	markProfileFeedActive(actorId) {
		if (actorId === _activeProfileActorId) {
			return;
		}
		const key = getPostsCacheKey(Constants.Feed.FeedType.PROFILE, actorId);
		delete _posts[key];
		delete _hasNextPage[key];
		_activeProfileActorId = actorId;
	},
	// A profile cache entry left behind by an earlier actor (one markProfileFeedActive hasn't
	// caught up to yet) is rejected here, at read time, rather than relying on it having been
	// cleared already.
	getPosts(feedType, actorId = null, signature = null) {
		if (!isPostsCacheUsable(feedType, actorId, signature)) {
			return [];
		}
		return _posts[getPostsCacheKey(feedType, actorId)] || [];
	},
	getHasNextPage(feedType, actorId = null, signature = null) {
		if (!isPostsCacheUsable(feedType, actorId, signature)) {
			return false;
		}
		return _hasNextPage[getPostsCacheKey(feedType, actorId)];
	},
	setSelectedPostId(id, feedType) {
		_selectedPostId[feedType] = id;
	},
	getSelectedPostId(feedType) {
		return _selectedPostId[feedType] || null;
	},
	setBlockVideoMute(isMuted) {
		_isBlockVideoMuted = isMuted;
	},
	getBlockVideoMute() {
		return _isBlockVideoMuted;
	},
	setVideoMute(isMuted) {
		_isVideoMuted = isMuted;
	},
	getVideoMute() {
		return _isVideoMuted;
	},
	getFeedBoughPhotosCount(callback) {
		const args = {
			guestId: Flux.Guest.getId(),
		};
		const query = `
	query FeedBoughtPhotosCount($guestId: Int!) {
        stats {
            boughtContentGuest(guestId: $guestId) {
                photos
            }
        }
    }
	`;
		doVxModelsRequest(query, { ...args }).then(callback);
	},
	getFeedBoughClipsCount(callback) {
		const args = {
			guestId: Flux.Guest.getId(),
		};
		const query = `
	query FeedBoughtClipsCount($guestId: Int!) {
        stats {
            boughtContentGuest(guestId: $guestId) {
                clips
            }
        }
    }
	`;
		doVxModelsRequest(query, { ...args }).then(callback);
	},

	// Direct method calls instead of actions
	toggleOverlay: function (postId, overlayType, ImageId) {
		this.emit(CHANGE_EVENTS.FeedOverlay, postId, overlayType, ImageId);
	},

	toggleLike: function (postId) {
		this.emit(CHANGE_EVENTS.FeedLike, postId);
	},

	triggerUpdatePost: function (postId) {
		this.emit(CHANGE_EVENTS.UpdatePost, postId);
	},

	triggerSetPosts: function () {
		this.emit(CHANGE_EVENTS.SetPosts);
	},

	toggleBlockVideoPlay: function (postId) {
		this.emit(CHANGE_EVENTS.VideoBlockPlay, postId);
	},

	setPlayingVideoId: function (postId) {
		this.emit(CHANGE_EVENTS.VideoPlay, postId, _isVideoMuted);
	},
});

const clipPost = `
... on ClipPost {
	clip {
		id
		length
		width
		height
		ratio
		avsRequired
		sources {
			label
			src
			type
		}
		poster {
			id
			url(size: W640)
			srcSet
		}
	}
}`;

const textPost = `
... on TextPost {
	id
	created
	published
	type
	status
	pinned
	title
	text
	views
	likes
	umaId
	__typename
}`;

const photoPost = `
... on PhotosPost {
	id
	created
	published
	type
	status
	pinned
	title
	text
	views
	likes
	umaId
    contest {
        id
    }
	photos {
	  id
	  url
	  srcSet
	  avsRequired
	}
  }`;

const user = () => `
	name
	id
	profile {
		avatar16: avatar(fsk: FSK16) {
			id
			url(size: W320)
			srcSet
			fsk
			avsRequired
		}
		avatar18: avatar(fsk: ${getGuestFSK()}) {
			id
			url(size: W320)
			srcSet
			fsk
			avsRequired
		}
		isVerified
		frame {
			imageUrl
		}
		badges {
			imageUrl
		}
	}
`;

const comment = () => `
	totalCount
	nodes {
		comment {
			id
			conversationId
			parentId
			created
			isCreator
			tipped
			text
			isReplyToReply
			user {
				${user()}
			}
		}
		replies {
			id
			conversationId
			parentId
			created
			isCreator
			tipped
			text
			isReplyToReply
			user {
				${user()}
			}
		}
	}
`;

const post = () => `
	__typename
	id
	created
	published
	model {
		online
        onlineState
		${user()}
	}
	type
	status
	pinned
	title
	text
	views
	likes
	umaId
	paidContent
	fsk
	${POST_PRICE}
	tippingGoal {
		${TIPPING_GOAL.goal}
		${TIPPING_GOAL.tipsSum}
		progressPercent
		achieved
	}
	guestState {
		liked
		viewed
		following
		bought
	}
	comments {
		${comment()}
	}
	${clipPost}
	${textPost}
	${photoPost}
`;

export default FeedStore;
